// Register page component
import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Field,
  Input,
  VStack,
  Heading,
  Text,
  Card,
  Badge,
  Image,
} from '@chakra-ui/react';
import { Alert } from '@chakra-ui/react';
import { useColorModeValue } from '../hooks/useColorModeValue';
import { FiAlertCircle, FiMail } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { RegisterData } from '../types';

type RegisterFormData = RegisterData & { confirmPassword: string };

const RegisterPage: React.FC = () => {
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const { register: registerUser, isLoading } = useAuth();
  const bg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  
  // Get selected plan from URL parameter
  const selectedPlan = searchParams.get('plan');

  // NOTE: subscription tier is NOT sent at registration — the backend always
  // creates new users as FREE_TRIAL; plan upgrades happen post-signup via billing.

  const schema = yup.object({
    first_name: yup.string().required(t('auth.firstNameRequired')),
    last_name: yup.string().required(t('auth.lastNameRequired')),
    email: yup.string().email(t('auth.invalidEmail')).required(t('auth.emailRequired')),
    password: yup
      .string()
      .min(8, t('auth.passwordMinLength'))
      .required(t('auth.passwordRequired')),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], t('auth.passwordsMustMatch'))
      .required(t('auth.confirmPasswordRequired')),
    organization: yup.string().optional(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegisterFormData>({
    resolver: yupResolver(schema) as any,
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      setError('');
      // Strip confirmPassword — it's only for client-side validation
      // Do NOT send subscription_tier: the backend always defaults new users to FREE_TRIAL
      const { confirmPassword: _confirmPassword, ...registerData } = data;

      await registerUser(registerData);
      setSuccess(true);
    } catch (err: any) {
      setError(
        err.response?.data?.detail || t('auth.registrationFailed')
      );
    }
  };

  if (success) {
    return (
      <Box bg={bg} minH="100vh" py={12}>
        <Container maxW="md">
          <Card.Root bg={cardBg}>
            <Card.Body>
              <VStack gap={6} textAlign="center">
                <Box
                  bg="green.50"
                  borderRadius="full"
                  p={4}
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <FiMail size={40} color="#38A169" />
                </Box>
                <Heading size="lg">{t('auth.checkYourEmail')}</Heading>
                <Text color="gray.600" fontSize="md">
                  {t('auth.verificationLinkSent')}
                </Text>
                <Text color="gray.500" fontSize="sm">
                  {t('auth.verificationLinkExpires')}
                </Text>
                <Button
                  onClick={() => { window.location.href = '/login'; }}
                  colorScheme="blue"
                  width="full"
                  mt={2}
                >
                  {t('auth.goToLogin')}
                </Button>
              </VStack>
            </Card.Body>
          </Card.Root>
        </Container>
      </Box>
    );
  }

  return (
    <Box bg={bg} minH="100vh" py={12}>
      <Container maxW="md">
        <Card.Root bg={cardBg}>
          <Card.Body>
            <VStack gap={8}>
              <VStack gap={2} align="center">
                <Image src="/neurev-logo.png" alt={t('auth.neurevLogoAlt')} height="80px" objectFit="contain" />
                <Heading size="lg" textAlign="center">
                  {t('auth.createAccountTitle')}
                </Heading>
                <Text textAlign="center" color="gray.600">
                  {t('auth.joinNeurevToday')}
                </Text>
                {selectedPlan && (
                  <Badge colorScheme="blue" size="lg" px={4} py={2}>
                    {t('auth.selectedPlan', { plan: selectedPlan.replace('_', ' ').toUpperCase() })}
                  </Badge>
                )}
              </VStack>

              {error && (
                <Alert.Root status="error" borderRadius="md">
                  <Alert.Indicator>
                    <FiAlertCircle />
                  </Alert.Indicator>
                  <Alert.Description>{error}</Alert.Description>
                </Alert.Root>
              )}

              <Box w="full">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <VStack gap={6}>
                    <Field.Root invalid={!!errors.first_name}>
                      <Field.Label>{t('auth.firstName')}</Field.Label>
                      <Input
                        type="text"
                        placeholder={t('auth.enterYourFirstName')}
                        {...register('first_name')}
                      />
                      {errors.first_name && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.first_name.message}
                        </Text>
                      )}
                    </Field.Root>

                    <Field.Root invalid={!!errors.last_name}>
                      <Field.Label>{t('auth.lastName')}</Field.Label>
                      <Input
                        type="text"
                        placeholder={t('auth.enterYourLastName')}
                        {...register('last_name')}
                      />
                      {errors.last_name && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.last_name.message}
                        </Text>
                      )}
                    </Field.Root>

                    <Field.Root>
                      <Field.Label>{t('auth.organizationOptional')}</Field.Label>
                      <Input
                        type="text"
                        placeholder={t('auth.enterYourOrganization')}
                        {...register('organization')}
                      />
                    </Field.Root>

                    <Field.Root invalid={!!errors.email}>
                      <Field.Label>{t('auth.emailAddress')}</Field.Label>
                      <Input
                        type="email"
                        placeholder={t('auth.enterYourEmail')}
                        {...register('email')}
                      />
                      {errors.email && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.email.message}
                        </Text>
                      )}
                    </Field.Root>

                    <Field.Root invalid={!!errors.password}>
                      <Field.Label>{t('auth.password')}</Field.Label>
                      <Input
                        type="password"
                        placeholder={t('auth.createPasswordPlaceholder')}
                        {...register('password')}
                      />
                      {errors.password && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.password.message}
                        </Text>
                      )}
                    </Field.Root>

                    <Field.Root invalid={!!errors.confirmPassword}>
                      <Field.Label>{t('auth.confirmPassword')}</Field.Label>
                      <Input
                        type="password"
                        placeholder={t('auth.confirmYourPassword')}
                        {...register('confirmPassword')}
                      />
                      {errors.confirmPassword && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.confirmPassword.message}
                        </Text>
                      )}
                    </Field.Root>

                    <Button
                      type="submit"
                      colorScheme="brand"
                      size="lg"
                      w="full"
                      loading={isLoading}
                      loadingText={t('auth.creatingAccount')}
                    >
                      {t('auth.createAccountButton')}
                    </Button>
                  </VStack>
                </form>
              </Box>

              <Text textAlign="center">
                {t('auth.alreadyHaveAccount')}{' '}
                <Button
                  variant="plain"
                  color="brand.600"
                  p={0}
                  h="auto"
                  minW="auto"
                  onClick={() => { window.location.href = '/login'; }}
                >
                  {t('auth.signInHere')}
                </Button>
              </Text>
            </VStack>
          </Card.Body>
        </Card.Root>
      </Container>
    </Box>
  );
};

export default RegisterPage;
